import { useEffect, useState, type DragEvent, type ReactNode } from 'react';
import { Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button, Card, CardContent, Chip, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, FormControlLabel, InputLabel, MenuItem, Modal, IconButton, Link, Select, Switch, TextField, Tooltip, Typography } from '@mui/material';
import { Show, SimpleShowLayout, useNotify, usePermissions, useRedirect, useRefresh, useShowController } from 'react-admin';
import CloseIcon from '@mui/icons-material/Close';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import BlockIcon from '@mui/icons-material/Block';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { getAdStatusMeta, normalizeAdStatus } from './adStatuses';
import { usersProxy } from './apiGateway';
import type { AppRole } from './accessControl';
import { AiModerationResultCard, type AiModeration } from './AiModerationResultCard';

type AdStatusInfo = {
    type: string;
    reason?: string | null;
    internalComment?: string | null;
    isFake?: boolean | null;
    orderId?: string | null;
    expertModerationExtraInfo?: {
        photos?: string[];
        userComment?: string | null;
    } | null;
};

type InternalAdInfoResponse = {
    id: string;
    adObjectId?: string;
    createdByUserId: string;
    adInfo: {
        gender: string;
        category: string;
        subcategory: string;
        attachmentPhotos: string[];
        photosId?: string[] | null;
        clothStatus: string;
        brandName: string;
        modelName: string;
        size: string;
        country: string;
        priceInfo: {
            fullPrice: number;
            amountWithoutFee: number;
            feeAmount: number;
            feePercent: number;
        };
        cityInfo: {
            id: string;
            city: string;
            region?: string | null;
        };
        otherPref: {
            isExchange: boolean;
            isVintage: boolean;
            isExistBox: boolean;
            serialNumber?: string | null;
        };
        description: string;
    };
    status: AdStatusInfo;
    statusHistory: AdStatusInfo[];
    hasOriginalLabel: boolean;
    creationDate: string;
    lastUpdateDate: string;
    aiModeration?: AiModeration | null;
    details: {
        serialNumber: number;
        viewsCount: number;
    };
};

type AdBlockEventItem = {
    id?: string;
    adId: string;
    action?: 'BLOCK' | 'UNBLOCK' | string;
    createdAt?: string | null;
    moderator?: {
        id?: string;
        name?: string;
        login?: string;
        role?: string;
    } | null;
};

type AdStatusEventActor = {
    userId?: string;
    login?: string;
    fullName?: string;
    role?: string;
} | null;

type AdStatusEventItem = {
    id?: string;
    statusType?: string;
    source?: 'FIRST_MODERATION' | 'EXPERT_MODERATION' | 'BLOCK' | 'MANUAL_STATUS_UPDATE' | 'AD_ATTRIBUTES_UPDATE' | string;
    resultCode?: string;
    reason?: string | null;
    createdAt?: string | null;
    actor?: AdStatusEventActor;
    attributeChanges?: {
        brandName?: { before: string; after: string };
        modelName?: { before: string; after: string };
        isExchange?: { before: boolean; after: boolean };
        isVintage?: { before: boolean; after: boolean };
        isExistBox?: { before: boolean; after: boolean };
        serialNumber?: { before: string; after: string };
        attachmentPhotos?: {
            beforeUrls: string[];
            afterUrls: string[];
        };
    } | null;
};

type AdStatusUpdateTarget = 'ACTIVE' | 'MODERATION' | 'EXPERT_MODERATION';
type OriginalLabelDialogValue = 'UNCHANGED' | 'SET_TRUE' | 'SET_FALSE';
type EditableAdPhotoItem = {
    id: string;
    url: string;
};

type PatchAdOtherPrefRequest = {
    attachmentPhotoIds?: string[];
    brandName?: string;
    modelName?: string;
    otherPref?: {
        isExchange?: boolean;
        isVintage?: boolean;
        isExistBox?: boolean;
        serialNumber?: string;
    };
};

type ChangePreviewItem =
    | {
        kind: 'text';
        label: string;
        before: string;
        after: string;
    }
    | {
        kind: 'boolean';
        label: string;
        before: boolean;
        after: boolean;
    }
    | {
        kind: 'photos';
        label: string;
        before: EditableAdPhotoItem[];
        after: EditableAdPhotoItem[];
    };

const formatValue = (value?: string | null) => {
    if (!value) {
        return 'Не указано';
    }
    return value;
};

const formatDateTime = (value?: string | null) => {
    if (!value) {
        return 'Не указано';
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return value;
    }
    return date.toLocaleString('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
    });
};

const formatPrice = (value?: number | null) => {
    if (!Number.isFinite(value)) {
        return 'Не указано';
    }
    return `${new Intl.NumberFormat('ru-RU').format(Number(value))} ₽`;
};

const formatBoolean = (value?: boolean | null) => (value ? 'Да' : 'Нет');

const formatActorLabel = (actor?: AdStatusEventActor) => {
    const fullName = (actor?.fullName || '').trim();
    const login = (actor?.login || '').trim();

    if (fullName && login) {
        return `${fullName} (${login})`;
    }

    return fullName || login || 'Не найдено';
};

const getStatusEventSourceLabel = (source?: string) => {
    const normalizedSource = normalizeAdStatus(source);
    if (normalizedSource === 'FIRST_MODERATION') {
        return 'Первичная модерация';
    }
    if (normalizedSource === 'EXPERT_MODERATION') {
        return 'Экспертная модерация';
    }
    if (normalizedSource === 'BLOCK') {
        return 'Блокировка объявления';
    }
    if (normalizedSource === 'MANUAL_STATUS_UPDATE') {
        return 'Изменение статуса через кнопку';
    }
    if (normalizedSource === 'AD_ATTRIBUTES_UPDATE') {
        return 'Изменение атрибутов объявления';
    }
    return formatValue(source);
};

const AD_BLOCK_FORBIDDEN_ROLES = new Set<AppRole>([
    'expert',
    'expert-admin',
    'expert-outstaff',
    'expert-admin-outstaff'
]);
const AD_STATUS_CHANGE_FORBIDDEN_STATUSES = new Set(['BLOCKED', 'DELETED', 'SOLD']);
const AD_STATUS_CHANGE_ALLOWED_ROLES = new Set<AppRole>(['moderator', 'moderator-admin', 'manager-admin']);

const buildPhotoItemsForEditor = (rawPhotoIds: unknown, rawPhotoUrls: unknown): EditableAdPhotoItem[] | null => {
    if (!Array.isArray(rawPhotoIds) || !Array.isArray(rawPhotoUrls) || rawPhotoIds.length !== rawPhotoUrls.length) {
        return null;
    }
    const items = rawPhotoIds.map((rawId, index) => {
        const id = typeof rawId === 'string' ? rawId.trim() : '';
        if (!id) {
            return null;
        }
        const url = typeof rawPhotoUrls[index] === 'string' ? rawPhotoUrls[index] : '';
        return { id, url };
    });

    if (items.some((item) => !item)) {
        return null;
    }

    return items as EditableAdPhotoItem[];
};

const arraysEqual = (left: string[], right: string[]) => (
    left.length === right.length && left.every((value, index) => value === right[index])
);

const resolveApiErrorStatus = (err: unknown) => {
    if (typeof err === 'object' && err && 'status' in err) {
        const status = (err as { status?: unknown }).status;
        if (typeof status === 'number') {
            return status;
        }
    }

    if (err instanceof Error) {
        const match = err.message.match(/API Error: (\d+)/);
        if (match) {
            return Number(match[1]);
        }
    }

    return null;
};

const ProfileRow = ({ label, value }: { label: string; value: ReactNode }) => (
    <Box sx={{ display: 'grid', gridTemplateColumns: '220px 1fr', gap: 2, py: 1 }}>
        <Typography color="text.secondary">{label}</Typography>
        <Typography sx={{ whiteSpace: 'pre-wrap' }}>{value}</Typography>
    </Box>
);

const StatusBlock = ({
    title,
    value,
    onPhotoClick,
    hideEmptyFields = false,
}: {
    title: string;
    value: AdStatusInfo | undefined;
    onPhotoClick?: (photos: string[], index: number) => void;
    hideEmptyFields?: boolean;
}) => {
    const statusMeta = getAdStatusMeta(value?.type);
    const expertPhotos = Array.isArray(value?.expertModerationExtraInfo?.photos)
        ? (value?.expertModerationExtraInfo?.photos || []).filter((photo) => typeof photo === 'string' && photo.trim())
        : [];
    const reason = typeof value?.reason === 'string' ? value.reason.trim() : '';
    const internalComment = typeof value?.internalComment === 'string' ? value.internalComment.trim() : '';
    const hasIsFake = value?.isFake != null;
    const orderId = typeof value?.orderId === 'string' ? value.orderId.trim() : '';
    const expertUserComment = typeof value?.expertModerationExtraInfo?.userComment === 'string'
        ? value.expertModerationExtraInfo.userComment.trim()
        : '';

    return (
        <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                <Typography fontWeight={600}>{title}</Typography>
                <Chip label={statusMeta.label} color={statusMeta.color} size="small" />
            </Box>
            {(!hideEmptyFields || reason) && (
                <ProfileRow label="Причина" value={formatValue(reason)} />
            )}
            {(!hideEmptyFields || internalComment) && (
                <ProfileRow label="Внутренний комментарий" value={formatValue(internalComment)} />
            )}
            {(!hideEmptyFields || hasIsFake) && (
                <ProfileRow label="Признак фейка" value={hasIsFake ? formatBoolean(Boolean(value?.isFake)) : 'Не указано'} />
            )}
            {(!hideEmptyFields || orderId) && (
                <ProfileRow label="Order ID" value={formatValue(orderId)} />
            )}
            {(!hideEmptyFields || expertUserComment) && (
                <ProfileRow label="Комментарий эксперта" value={formatValue(expertUserComment)} />
            )}
            {expertPhotos.length > 0 && (
                <Box sx={{ mt: 1 }}>
                    <Typography color="text.secondary" sx={{ mb: 1 }}>
                        Фото эксперта
                    </Typography>
                    <Box sx={{ display: 'grid', gap: 1, gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))' } }}>
                        {expertPhotos.map((photoUrl, index) => (
                            <Box
                                key={`${photoUrl}-${index}`}
                                component="img"
                                src={photoUrl}
                                alt={`Экспертное фото ${index + 1}`}
                                onClick={() => onPhotoClick && onPhotoClick(expertPhotos, index)}
                                sx={{
                                    width: '100%',
                                    height: 140,
                                    objectFit: 'cover',
                                    borderRadius: 1.5,
                                    cursor: 'pointer',
                                    transition: 'opacity 0.2s ease',
                                    '&:hover': {
                                        opacity: 0.85
                                    }
                                }}
                            />
                        ))}
                    </Box>
                </Box>
            )}
        </Box>
    );
};

const PhotoOrderPreview = ({
    title,
    photos,
    onPhotoClick
}: {
    title: string;
    photos: EditableAdPhotoItem[];
    onPhotoClick?: (photos: string[], index: number) => void;
}) => {
    if (!photos.length) {
        return null;
    }

    const photoUrls = photos.map((item) => item.url).filter((url) => Boolean(url));

    return (
        <Box>
            <Typography color="text.secondary" sx={{ mb: 0.75 }}>{title}</Typography>
            <Box sx={{ display: 'grid', gap: 1, gridTemplateColumns: { xs: 'repeat(4, minmax(0, 1fr))', md: 'repeat(8, minmax(0, 1fr))' } }}>
                {photos.map((photoItem, index) => (
                    <Box key={`${photoItem.id}-${index}`} sx={{ position: 'relative' }}>
                        {photoItem.url ? (
                            <Box
                                component="img"
                                src={photoItem.url}
                                alt={`Фото ${index + 1}`}
                                onClick={() => onPhotoClick && photoUrls.length > 0 && onPhotoClick(photoUrls, photoUrls.indexOf(photoItem.url))}
                                sx={{
                                    width: '100%',
                                    aspectRatio: '1 / 1',
                                    objectFit: 'cover',
                                    borderRadius: 1,
                                    cursor: onPhotoClick ? 'pointer' : 'default'
                                }}
                            />
                        ) : (
                            <Box
                                sx={{
                                    width: '100%',
                                    aspectRatio: '1 / 1',
                                    borderRadius: 1,
                                    bgcolor: 'action.hover',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: 'text.secondary',
                                    fontSize: 12
                                }}
                            >
                                Без фото
                            </Box>
                        )}
                        <Chip
                            label={index + 1}
                            size="small"
                            sx={{
                                position: 'absolute',
                                top: 4,
                                left: 4,
                                height: 20,
                                fontSize: 11,
                                bgcolor: 'rgba(0, 0, 0, 0.7)',
                                color: 'white'
                            }}
                        />
                    </Box>
                ))}
            </Box>
        </Box>
    );
};

export const UserAdShow = () => {
    const { record, isLoading, error } = useShowController<InternalAdInfoResponse>();
    const { permissions } = usePermissions();
    const notify = useNotify();
    const redirect = useRedirect();
    const refresh = useRefresh();
    const [photoModalOpen, setPhotoModalOpen] = useState(false);
    const [modalPhotos, setModalPhotos] = useState<string[]>([]);
    const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
    const [blockDialogOpen, setBlockDialogOpen] = useState(false);
    const [unblockDialogOpen, setUnblockDialogOpen] = useState(false);
    const [blockReason, setBlockReason] = useState('');
    const [blockInternalComment, setBlockInternalComment] = useState('');
    const [blockDialogError, setBlockDialogError] = useState<string | null>(null);
    const [blockSubmitting, setBlockSubmitting] = useState(false);
    const [statusDialogOpen, setStatusDialogOpen] = useState(false);
    const [statusDialogValue, setStatusDialogValue] = useState<AdStatusUpdateTarget>('MODERATION');
    const [statusDialogReason, setStatusDialogReason] = useState('');
    const [statusDialogOriginalLabel, setStatusDialogOriginalLabel] = useState<OriginalLabelDialogValue>('UNCHANGED');
    const [statusDialogError, setStatusDialogError] = useState<string | null>(null);
    const [statusDialogSubmitting, setStatusDialogSubmitting] = useState(false);
    const [blockEvents, setBlockEvents] = useState<AdBlockEventItem[]>([]);
    const [blockEventsLoading, setBlockEventsLoading] = useState(false);
    const [statusEvents, setStatusEvents] = useState<AdStatusEventItem[]>([]);
    const [statusEventsLoading, setStatusEventsLoading] = useState(false);
    const [statusEventsExpanded, setStatusEventsExpanded] = useState(false);
    const [statusEventsLoadedAdId, setStatusEventsLoadedAdId] = useState<string | null>(null);
    const [statusEventsLoadedReloadKey, setStatusEventsLoadedReloadKey] = useState<number | null>(null);
    const [blockEventsReloadKey, setBlockEventsReloadKey] = useState(0);
    const [blockHistoryExpanded, setBlockHistoryExpanded] = useState(false);
    const [attributesExpanded, setAttributesExpanded] = useState(false);
    const [editBrandName, setEditBrandName] = useState('');
    const [editModelName, setEditModelName] = useState('');
    const [editIsExchange, setEditIsExchange] = useState(false);
    const [editIsVintage, setEditIsVintage] = useState(false);
    const [editIsExistBox, setEditIsExistBox] = useState(false);
    const [editSerialNumber, setEditSerialNumber] = useState('');
    const [editablePhotos, setEditablePhotos] = useState<EditableAdPhotoItem[]>([]);
    const [photoDragId, setPhotoDragId] = useState<string | null>(null);
    const [photoDragOverId, setPhotoDragOverId] = useState<string | null>(null);
    const [attributesConfirmOpen, setAttributesConfirmOpen] = useState(false);
    const [attributesSubmitting, setAttributesSubmitting] = useState(false);
    const [attributesSubmitError, setAttributesSubmitError] = useState<string | null>(null);
    const adIdForBlockAction = record?.adObjectId || record?.id || '';

    useEffect(() => {
        if (!record) {
            return;
        }

        const sourcePhotoItems = buildPhotoItemsForEditor(record.adInfo?.photosId, record.adInfo?.attachmentPhotos);
        setEditBrandName(record.adInfo?.brandName || '');
        setEditModelName(record.adInfo?.modelName || '');
        setEditIsExchange(Boolean(record.adInfo?.otherPref?.isExchange));
        setEditIsVintage(Boolean(record.adInfo?.otherPref?.isVintage));
        setEditIsExistBox(Boolean(record.adInfo?.otherPref?.isExistBox));
        setEditSerialNumber(record.adInfo?.otherPref?.serialNumber || '');
        setEditablePhotos(sourcePhotoItems || []);
        setPhotoDragId(null);
        setPhotoDragOverId(null);
        setAttributesConfirmOpen(false);
        setAttributesSubmitError(null);
    }, [record]);

    useEffect(() => {
        if (!adIdForBlockAction) {
            setStatusEvents([]);
            setStatusEventsLoadedAdId(null);
            setStatusEventsLoadedReloadKey(null);
            return;
        }

        if (!statusEventsExpanded) {
            return;
        }

        const hasFreshStatusEvents = statusEventsLoadedAdId === adIdForBlockAction
            && statusEventsLoadedReloadKey === blockEventsReloadKey;
        if (hasFreshStatusEvents) {
            return;
        }

        let isCancelled = false;
        setStatusEventsLoading(true);

        usersProxy.getAdStatusEvents({ adId: adIdForBlockAction, limit: 100 })
            .then((response) => {
                if (isCancelled) {
                    return;
                }
                const items = Array.isArray(response?.items) ? response.items : [];
                setStatusEvents(items as AdStatusEventItem[]);
                setStatusEventsLoadedAdId(adIdForBlockAction);
                setStatusEventsLoadedReloadKey(blockEventsReloadKey);
            })
            .catch((loadStatusEventsError) => {
                if (isCancelled) {
                    return;
                }
                setStatusEvents([]);
                console.error('Error loading ad status events:', loadStatusEventsError);
            })
            .finally(() => {
                if (isCancelled) {
                    return;
                }
                setStatusEventsLoading(false);
            });

        return () => {
            isCancelled = true;
        };
    }, [
        adIdForBlockAction,
        blockEventsReloadKey,
        statusEventsExpanded,
        statusEventsLoadedAdId,
        statusEventsLoadedReloadKey
    ]);

    useEffect(() => {
        if (!adIdForBlockAction) {
            setBlockEvents([]);
            return;
        }

        let isCancelled = false;
        setBlockEventsLoading(true);

        usersProxy.getAdBlockEvents({ adId: adIdForBlockAction, limit: 100 })
            .then((response) => {
                if (isCancelled) {
                    return;
                }
                const items = Array.isArray(response?.items) ? response.items : [];
                setBlockEvents(items as AdBlockEventItem[]);
            })
            .catch((loadBlockEventsError) => {
                if (isCancelled) {
                    return;
                }
                setBlockEvents([]);
                console.error('Error loading ad block events:', loadBlockEventsError);
            })
            .finally(() => {
                if (isCancelled) {
                    return;
                }
                setBlockEventsLoading(false);
            });

        return () => {
            isCancelled = true;
        };
    }, [adIdForBlockAction, blockEventsReloadKey]);

    if (isLoading) {
        return <div>Загрузка...</div>;
    }

    if (error) {
        notify('Ошибка при загрузке объявления', { type: 'error' });
        return <div>Ошибка при загрузке данных</div>;
    }

    if (!record) {
        return <div>Данные не найдены</div>;
    }

    const photos = Array.isArray(record.adInfo?.attachmentPhotos) ? record.adInfo.attachmentPhotos : [];
    const validPhotos = photos.filter((photo) => typeof photo === 'string' && photo.trim());
    const currentStatusMeta = getAdStatusMeta(record.status?.type);
    const statusHistory = Array.isArray(record.statusHistory) ? record.statusHistory : [];
    const city = record.adInfo?.cityInfo?.city || '';
    const region = record.adInfo?.cityInfo?.region || '';
    const cityLabel = [city, region].filter(Boolean).join(', ');
    const hasOriginalLabel = Boolean(record.hasOriginalLabel);
    const normalizedCurrentStatus = normalizeAdStatus(record.status?.type);
    const isBlocked = normalizedCurrentStatus === 'BLOCKED';
    const isStatusChangeForbiddenByCurrentStatus = AD_STATUS_CHANGE_FORBIDDEN_STATUSES.has(normalizedCurrentStatus);
    const currentRole = (permissions || null) as AppRole | null;
    const canManageAdBlock = !currentRole || !AD_BLOCK_FORBIDDEN_ROLES.has(currentRole);
    const canManageAdStatus = currentRole ? AD_STATUS_CHANGE_ALLOWED_ROLES.has(currentRole) : false;
    const disableStatusChangeButton = statusDialogSubmitting || blockSubmitting || isStatusChangeForbiddenByCurrentStatus;
    const statusChangeDisabledHint = isStatusChangeForbiddenByCurrentStatus
        ? `Для объявления в статусе «${currentStatusMeta.label}» нельзя установить другой статус`
        : '';
    const currentStatusEvent = statusEvents.find(
        (statusEvent) => normalizeAdStatus(statusEvent.statusType) === normalizeAdStatus(record.status?.type)
    ) || null;
    const hasLoadedStatusEvents = statusEventsLoadedAdId === adIdForBlockAction;
    const canManageAdAttributes = currentRole ? AD_STATUS_CHANGE_ALLOWED_ROLES.has(currentRole) : false;
    const originalPhotoItems = buildPhotoItemsForEditor(record.adInfo?.photosId, photos);
    const canReorderPhotos = Array.isArray(originalPhotoItems);
    const hasPhotoReorderSource = Boolean(originalPhotoItems && originalPhotoItems.length > 1);
    const editedPhotoItems = originalPhotoItems
        ? (editablePhotos.length === originalPhotoItems.length ? editablePhotos : originalPhotoItems)
        : [];
    const originalBrandName = record.adInfo?.brandName || '';
    const originalModelName = record.adInfo?.modelName || '';
    const originalSerialNumber = record.adInfo?.otherPref?.serialNumber || '';
    const brandNameChanged = editBrandName !== originalBrandName;
    const modelNameChanged = editModelName !== originalModelName;
    const serialNumberChanged = editSerialNumber !== originalSerialNumber;
    const brandNameError = brandNameChanged && !editBrandName.trim()
        ? 'Название бренда не может быть пустым'
        : '';
    const modelNameError = modelNameChanged && !editModelName.trim()
        ? 'Модель не может быть пустой'
        : '';
    const serialNumberError = serialNumberChanged && !editSerialNumber.trim()
        ? 'Серийный номер не может быть пустым'
        : '';
    const photoOrderChanged = canReorderPhotos
        && originalPhotoItems
        && !arraysEqual(
            originalPhotoItems.map((item) => item.id),
            editedPhotoItems.map((item) => item.id)
        );
    const patchAdOtherPrefRequest: PatchAdOtherPrefRequest = {};
    const changePreviewItems: ChangePreviewItem[] = [];

    if (photoOrderChanged) {
        patchAdOtherPrefRequest.attachmentPhotoIds = editedPhotoItems.map((item) => item.id);
        changePreviewItems.push({
            kind: 'photos',
            label: 'Порядок фотографий',
            before: originalPhotoItems || [],
            after: editedPhotoItems
        });
    }

    if (brandNameChanged && !brandNameError) {
        patchAdOtherPrefRequest.brandName = editBrandName;
        changePreviewItems.push({
            kind: 'text',
            label: 'Название бренда',
            before: originalBrandName,
            after: editBrandName
        });
    }

    if (modelNameChanged && !modelNameError) {
        patchAdOtherPrefRequest.modelName = editModelName;
        changePreviewItems.push({
            kind: 'text',
            label: 'Модель',
            before: originalModelName,
            after: editModelName
        });
    }

    const patchOtherPref: PatchAdOtherPrefRequest['otherPref'] = {};
    const originalIsExchange = Boolean(record.adInfo?.otherPref?.isExchange);
    const originalIsVintage = Boolean(record.adInfo?.otherPref?.isVintage);
    const originalIsExistBox = Boolean(record.adInfo?.otherPref?.isExistBox);

    if (editIsExchange !== originalIsExchange) {
        patchOtherPref.isExchange = editIsExchange;
        changePreviewItems.push({
            kind: 'boolean',
            label: 'Возможен обмен',
            before: originalIsExchange,
            after: editIsExchange
        });
    }
    if (editIsVintage !== originalIsVintage) {
        patchOtherPref.isVintage = editIsVintage;
        changePreviewItems.push({
            kind: 'boolean',
            label: 'Винтаж',
            before: originalIsVintage,
            after: editIsVintage
        });
    }
    if (editIsExistBox !== originalIsExistBox) {
        patchOtherPref.isExistBox = editIsExistBox;
        changePreviewItems.push({
            kind: 'boolean',
            label: 'Есть коробка',
            before: originalIsExistBox,
            after: editIsExistBox
        });
    }
    if (serialNumberChanged && !serialNumberError) {
        patchOtherPref.serialNumber = editSerialNumber;
        changePreviewItems.push({
            kind: 'text',
            label: 'Серийный номер',
            before: originalSerialNumber,
            after: editSerialNumber
        });
    }
    if (Object.keys(patchOtherPref).length > 0) {
        patchAdOtherPrefRequest.otherPref = patchOtherPref;
    }

    const hasAttributesValidationError = Boolean(brandNameError || modelNameError || serialNumberError);
    const hasAttributesChanges = Object.keys(patchAdOtherPrefRequest).length > 0;
    const disableAttributesSaveButton = !hasAttributesChanges
        || hasAttributesValidationError
        || attributesSubmitting
        || blockSubmitting
        || statusDialogSubmitting;

    const openPhotoModal = (photosList: string[], index: number) => {
        const normalizedPhotos = photosList.filter((photo) => typeof photo === 'string' && photo.trim());
        if (!normalizedPhotos.length) {
            return;
        }

        setModalPhotos(normalizedPhotos);
        setCurrentPhotoIndex(Math.max(0, Math.min(index, normalizedPhotos.length - 1)));
        setPhotoModalOpen(true);
    };

    const handleClosePhotoModal = () => {
        setPhotoModalOpen(false);
        setModalPhotos([]);
        setCurrentPhotoIndex(0);
    };

    const handlePrevPhoto = () => {
        if (!modalPhotos.length) {
            return;
        }
        setCurrentPhotoIndex((prev) => (prev - 1 + modalPhotos.length) % modalPhotos.length);
    };

    const handleNextPhoto = () => {
        if (!modalPhotos.length) {
            return;
        }
        setCurrentPhotoIndex((prev) => (prev + 1) % modalPhotos.length);
    };

    const reorderEditablePhotoItems = (fromId: string, toId: string) => {
        if (!canReorderPhotos || !fromId || !toId || fromId === toId) {
            return;
        }

        setEditablePhotos((previousItems) => {
            const fromIndex = previousItems.findIndex((item) => item.id === fromId);
            const toIndex = previousItems.findIndex((item) => item.id === toId);
            if (fromIndex < 0 || toIndex < 0 || fromIndex === toIndex) {
                return previousItems;
            }

            const swappedItems = [...previousItems];
            const fromItem = swappedItems[fromIndex];
            swappedItems[fromIndex] = swappedItems[toIndex];
            swappedItems[toIndex] = fromItem;
            return swappedItems;
        });
    };

    const moveEditablePhotoByOffset = (photoId: string, offset: number) => {
        if (!canReorderPhotos || !offset) {
            return;
        }

        setEditablePhotos((previousItems) => {
            const fromIndex = previousItems.findIndex((item) => item.id === photoId);
            if (fromIndex < 0) {
                return previousItems;
            }
            const toIndex = fromIndex + offset;
            if (toIndex < 0 || toIndex >= previousItems.length) {
                return previousItems;
            }

            const reorderedItems = [...previousItems];
            const [movedItem] = reorderedItems.splice(fromIndex, 1);
            reorderedItems.splice(toIndex, 0, movedItem);
            return reorderedItems;
        });
    };

    const handlePhotoDragStart = (photoId: string) => {
        if (!canReorderPhotos) {
            return;
        }
        setPhotoDragId(photoId);
        setPhotoDragOverId(photoId);
    };

    const handlePhotoDragOver = (photoId: string, event: DragEvent<HTMLDivElement>) => {
        if (!canReorderPhotos || !photoDragId || photoDragId === photoId) {
            return;
        }

        event.preventDefault();
        setPhotoDragOverId(photoId);
    };

    const handlePhotoDrop = (photoId: string, event: DragEvent<HTMLDivElement>) => {
        if (!canReorderPhotos || !photoDragId) {
            return;
        }

        event.preventDefault();
        reorderEditablePhotoItems(photoDragId, photoId);
        setPhotoDragId(null);
        setPhotoDragOverId(null);
    };

    const handlePhotoDragEnd = () => {
        setPhotoDragId(null);
        setPhotoDragOverId(null);
    };

    const handleOpenAttributesConfirmDialog = () => {
        if (disableAttributesSaveButton) {
            return;
        }
        setAttributesSubmitError(null);
        setAttributesConfirmOpen(true);
    };

    const handleCloseAttributesConfirmDialog = () => {
        if (attributesSubmitting) {
            return;
        }
        setAttributesConfirmOpen(false);
        setAttributesSubmitError(null);
    };

    const handleSubmitAttributesUpdate = async () => {
        if (!adIdForBlockAction || disableAttributesSaveButton) {
            return;
        }

        setAttributesSubmitting(true);
        setAttributesSubmitError(null);

        try {
            await usersProxy.patchAdOtherPref({
                adId: adIdForBlockAction,
                ...patchAdOtherPrefRequest
            });
            setAttributesConfirmOpen(false);
            notify('Атрибуты объявления обновлены', { type: 'success' });
            setBlockEventsReloadKey((prev) => prev + 1);
            refresh();
        } catch (submitAttributesError) {
            const status = resolveApiErrorStatus(submitAttributesError);
            const errorMessage = status === 404
                ? 'Объявление не найдено'
                : status === 400
                    ? 'Не удалось обновить атрибуты: проверьте изменения'
                    : 'Не удалось обновить атрибуты объявления';
            setAttributesSubmitError(errorMessage);
            notify(errorMessage, { type: 'warning' });
            console.error('Error updating ad attributes:', submitAttributesError);
        } finally {
            setAttributesSubmitting(false);
        }
    };

    const handleOpenBlockDialog = () => {
        if (blockSubmitting) {
            return;
        }
        setBlockReason('');
        setBlockInternalComment('');
        setBlockDialogError(null);
        setBlockDialogOpen(true);
    };

    const handleCloseBlockDialog = () => {
        if (blockSubmitting) {
            return;
        }
        setBlockDialogOpen(false);
        setBlockDialogError(null);
    };

    const handleOpenUnblockDialog = () => {
        if (blockSubmitting) {
            return;
        }
        setBlockDialogError(null);
        setUnblockDialogOpen(true);
    };

    const handleCloseUnblockDialog = () => {
        if (blockSubmitting) {
            return;
        }
        setUnblockDialogOpen(false);
        setBlockDialogError(null);
    };

    const handleSubmitBlock = async () => {
        if (!adIdForBlockAction || blockSubmitting) {
            return;
        }

        const normalizedReason = blockReason.trim();
        const normalizedInternalComment = blockInternalComment.trim();
        if (!normalizedReason) {
            setBlockDialogError('Укажите причину блокировки для пользователя');
            return;
        }
        if (!normalizedInternalComment) {
            setBlockDialogError('Укажите внутренний комментарий');
            return;
        }

        setBlockSubmitting(true);
        setBlockDialogError(null);
        try {
            await usersProxy.changeAdBlockStatus({
                adId: adIdForBlockAction,
                action: 'BLOCK',
                reason: normalizedReason,
                internalComment: normalizedInternalComment
            });
            setBlockDialogOpen(false);
            setBlockReason('');
            setBlockInternalComment('');
            notify('Объявление заблокировано', { type: 'success' });
            setBlockEventsReloadKey((prev) => prev + 1);
            refresh();
        } catch (submitBlockError) {
            const status = resolveApiErrorStatus(submitBlockError);
            const errorMessage = status === 404 ? 'Объявление не найдено' : 'Не удалось заблокировать объявление';
            setBlockDialogError(errorMessage);
            notify(errorMessage, { type: 'warning' });
            console.error('Error blocking ad:', submitBlockError);
        } finally {
            setBlockSubmitting(false);
        }
    };

    const handleSubmitUnblock = async () => {
        if (!adIdForBlockAction || blockSubmitting) {
            return;
        }

        setBlockSubmitting(true);
        setBlockDialogError(null);
        try {
            await usersProxy.changeAdBlockStatus({
                adId: adIdForBlockAction,
                action: 'UNBLOCK'
            });
            setUnblockDialogOpen(false);
            notify('Объявление разблокировано', { type: 'success' });
            setBlockEventsReloadKey((prev) => prev + 1);
            refresh();
        } catch (submitUnblockError) {
            const status = resolveApiErrorStatus(submitUnblockError);
            const errorMessage = status === 404 ? 'Объявление не найдено' : 'Не удалось разблокировать объявление';
            setBlockDialogError(errorMessage);
            notify(errorMessage, { type: 'warning' });
            console.error('Error unblocking ad:', submitUnblockError);
        } finally {
            setBlockSubmitting(false);
        }
    };

    const handleOpenStatusDialog = () => {
        if (statusDialogSubmitting || blockSubmitting || isStatusChangeForbiddenByCurrentStatus) {
            return;
        }

        const suggestedStatus: AdStatusUpdateTarget = normalizedCurrentStatus === 'ACTIVE'
            ? 'MODERATION'
            : 'ACTIVE';
        setStatusDialogValue(suggestedStatus);
        setStatusDialogReason('');
        setStatusDialogOriginalLabel('UNCHANGED');
        setStatusDialogError(null);
        setStatusDialogOpen(true);
    };

    const handleCloseStatusDialog = () => {
        if (statusDialogSubmitting) {
            return;
        }
        setStatusDialogOpen(false);
        setStatusDialogError(null);
    };

    const handleSubmitStatus = async () => {
        if (!adIdForBlockAction || statusDialogSubmitting) {
            return;
        }

        const activeOriginalLabelChangeRequested = statusDialogValue === 'ACTIVE'
            && (
                (statusDialogOriginalLabel === 'SET_TRUE' && !hasOriginalLabel)
                || (statusDialogOriginalLabel === 'SET_FALSE' && hasOriginalLabel)
            );
        if (statusDialogValue === normalizedCurrentStatus && !activeOriginalLabelChangeRequested) {
            setStatusDialogError('Нельзя выставить такой же статус, как текущий');
            return;
        }

        const normalizedReason = statusDialogReason.trim();
        if (!normalizedReason) {
            setStatusDialogError('Укажите причину изменения статуса');
            return;
        }

        setStatusDialogSubmitting(true);
        setStatusDialogError(null);
        try {
            if (statusDialogValue === 'ACTIVE') {
                await usersProxy.setAdStatus({
                    adId: adIdForBlockAction,
                    status: 'ACTIVE',
                    reason: normalizedReason,
                    ...(statusDialogOriginalLabel === 'SET_TRUE'
                        ? { hasOriginalLabel: true }
                        : statusDialogOriginalLabel === 'SET_FALSE'
                            ? { hasOriginalLabel: false }
                            : {})
                });
            } else if (statusDialogValue === 'MODERATION') {
                await usersProxy.setAdStatus({
                    adId: adIdForBlockAction,
                    status: 'MODERATION',
                    reason: normalizedReason
                });
            } else {
                await usersProxy.setAdStatus({
                    adId: adIdForBlockAction,
                    status: 'EXPERT_MODERATION',
                    reason: normalizedReason
                });
            }

            const successMessage = statusDialogValue === 'ACTIVE'
                ? 'Статус изменен на «Активно»'
                : statusDialogValue === 'MODERATION'
                    ? 'Объявление отправлено на модерацию'
                    : 'Объявление отправлено экспертам';
            notify(successMessage, { type: 'success' });
            setStatusDialogOpen(false);
            setStatusDialogReason('');
            setStatusDialogOriginalLabel('UNCHANGED');
            setBlockEventsReloadKey((prev) => prev + 1);
            refresh();
        } catch (submitStatusError) {
            const status = resolveApiErrorStatus(submitStatusError);
            const errorText = submitStatusError instanceof Error ? submitStatusError.message : '';
            const isSameStatusError = /same status|такой же статус/i.test(errorText);
            const errorMessage = status === 404
                ? 'Объявление не найдено'
                : isSameStatusError
                    ? 'Нельзя выставить такой же статус, как текущий'
                : status === 400
                    ? 'Не удалось изменить статус: проверьте параметры'
                    : 'Не удалось изменить статус объявления';
            setStatusDialogError(errorMessage);
            notify(errorMessage, { type: 'warning' });
            console.error('Error changing ad status:', submitStatusError);
        } finally {
            setStatusDialogSubmitting(false);
        }
    };

    const renderAttributeChangeRows = (statusEvent: AdStatusEventItem) => {
        const attributeChanges = statusEvent.attributeChanges;
        if (!attributeChanges) {
            return null;
        }

        return (
            <Box sx={{ display: 'grid', gap: 1 }}>
                {attributeChanges.brandName ? (
                    <ProfileRow
                        label="Бренд"
                        value={`${formatValue(attributeChanges.brandName.before)} → ${formatValue(attributeChanges.brandName.after)}`}
                    />
                ) : null}
                {attributeChanges.modelName ? (
                    <ProfileRow
                        label="Модель"
                        value={`${formatValue(attributeChanges.modelName.before)} → ${formatValue(attributeChanges.modelName.after)}`}
                    />
                ) : null}
                {attributeChanges.isExchange ? (
                    <ProfileRow
                        label="Возможен обмен"
                        value={`${formatBoolean(attributeChanges.isExchange.before)} → ${formatBoolean(attributeChanges.isExchange.after)}`}
                    />
                ) : null}
                {attributeChanges.isVintage ? (
                    <ProfileRow
                        label="Винтаж"
                        value={`${formatBoolean(attributeChanges.isVintage.before)} → ${formatBoolean(attributeChanges.isVintage.after)}`}
                    />
                ) : null}
                {attributeChanges.isExistBox ? (
                    <ProfileRow
                        label="Есть коробка"
                        value={`${formatBoolean(attributeChanges.isExistBox.before)} → ${formatBoolean(attributeChanges.isExistBox.after)}`}
                    />
                ) : null}
                {attributeChanges.serialNumber ? (
                    <ProfileRow
                        label="Серийный номер"
                        value={`${formatValue(attributeChanges.serialNumber.before)} → ${formatValue(attributeChanges.serialNumber.after)}`}
                    />
                ) : null}
                {attributeChanges.attachmentPhotos ? (
                    <Box sx={{ pt: 0.5 }}>
                        <PhotoOrderPreview
                            title="Порядок фото: было"
                            photos={attributeChanges.attachmentPhotos.beforeUrls.map((url, index) => ({
                                id: `before-${index}`,
                                url
                            }))}
                            onPhotoClick={openPhotoModal}
                        />
                        <Box sx={{ mt: 1 }}>
                            <PhotoOrderPreview
                                title="Порядок фото: стало"
                                photos={attributeChanges.attachmentPhotos.afterUrls.map((url, index) => ({
                                    id: `after-${index}`,
                                    url
                                }))}
                                onPhotoClick={openPhotoModal}
                            />
                        </Box>
                    </Box>
                ) : null}
            </Box>
        );
    };

    return (
        <Show title={`Объявление ${record.adInfo?.brandName || ''} ${record.adInfo?.modelName || ''}`.trim()}>
            <SimpleShowLayout>
                <Box sx={{ display: 'grid', gap: 2 }}>
                    <Card>
                        <CardContent>
                            <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: '280px 1fr' } }}>
                                {validPhotos[0] ? (
                                    <Box
                                        component="img"
                                        src={validPhotos[0]}
                                        alt={`${record.adInfo?.brandName || 'Объявление'} ${record.adInfo?.modelName || ''}`}
                                        onClick={() => openPhotoModal(validPhotos, 0)}
                                        sx={{
                                            width: '100%',
                                            height: 260,
                                            borderRadius: 2,
                                            objectFit: 'cover',
                                            bgcolor: 'action.hover',
                                            cursor: 'pointer',
                                            transition: 'opacity 0.2s ease',
                                            '&:hover': {
                                                opacity: 0.85
                                            }
                                        }}
                                    />
                                ) : (
                                    <Box
                                        sx={{
                                            width: '100%',
                                            height: 260,
                                            borderRadius: 2,
                                            bgcolor: 'action.hover',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            color: 'text.secondary'
                                        }}
                                    >
                                        Без фото
                                    </Box>
                                )}
                                <Box sx={{ display: 'grid', gap: 1 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                        <Typography variant="h5" fontWeight={700}>
                                            {formatValue(record.adInfo?.brandName)} {formatValue(record.adInfo?.modelName)}
                                        </Typography>
                                        <Chip label={currentStatusMeta.label} color={currentStatusMeta.color} size="small" />
                                        <Chip
                                            label={hasOriginalLabel ? 'Оригинальный лейбл' : 'Лейбл отсутствует'}
                                            color={hasOriginalLabel ? 'success' : 'default'}
                                            size="small"
                                            sx={
                                                hasOriginalLabel
                                                    ? undefined
                                                    : {
                                                        bgcolor: '#111111',
                                                        color: '#ffffff',
                                                        '.MuiChip-label': { color: '#ffffff' }
                                                    }
                                            }
                                        />
                                    </Box>
                                    <Typography variant="h6">{formatPrice(record.adInfo?.priceInfo?.fullPrice)}</Typography>
                                    <Typography color="text.secondary">Город: {formatValue(cityLabel)}</Typography>
                                    <Typography color="text.secondary">
                                        Категория: {formatValue(record.adInfo?.category)} / {formatValue(record.adInfo?.subcategory)}
                                    </Typography>
                                    <Typography sx={{ whiteSpace: 'pre-wrap' }}>
                                        {formatValue(record.adInfo?.description)}
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>

                    <AiModerationResultCard aiModeration={record.aiModeration} />

                    <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
                        <Card>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>
                                    Параметры товара
                                </Typography>
                                <ProfileRow label="Состояние" value={formatValue(record.adInfo?.clothStatus)} />
                                <ProfileRow label="Пол" value={formatValue(record.adInfo?.gender)} />
                                <ProfileRow label="Размер" value={formatValue(record.adInfo?.size)} />
                                <ProfileRow label="Страна" value={formatValue(record.adInfo?.country)} />
                                <ProfileRow label="Возможен обмен" value={formatBoolean(record.adInfo?.otherPref?.isExchange)} />
                                <ProfileRow label="Винтаж" value={formatBoolean(record.adInfo?.otherPref?.isVintage)} />
                                <ProfileRow label="Есть коробка" value={formatBoolean(record.adInfo?.otherPref?.isExistBox)} />
                                <ProfileRow label="Серийный номер (item)" value={formatValue(record.adInfo?.otherPref?.serialNumber)} />
                            </CardContent>
                        </Card>

                        <Card>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>
                                    Цена и метрики
                                </Typography>
                                <ProfileRow label="Полная цена" value={formatPrice(record.adInfo?.priceInfo?.fullPrice)} />
                                <ProfileRow label="Сумма без комиссии" value={formatPrice(record.adInfo?.priceInfo?.amountWithoutFee)} />
                                <ProfileRow label="Комиссия" value={formatPrice(record.adInfo?.priceInfo?.feeAmount)} />
                                <ProfileRow label="Процент комиссии" value={`${record.adInfo?.priceInfo?.feePercent ?? 0}%`} />
                                <ProfileRow label="Просмотры" value={record.details?.viewsCount ?? 'Не указано'} />
                                <ProfileRow label="Серийный номер (ad)" value={record.details?.serialNumber ?? 'Не указано'} />
                            </CardContent>
                        </Card>
                    </Box>

                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Фото объявления
                            </Typography>
                            {validPhotos.length === 0 ? (
                                <Typography color="text.secondary">Фотографии не найдены</Typography>
                            ) : (
                                <Box sx={{ display: 'grid', gap: 1, gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))' } }}>
                                    {validPhotos.map((photoUrl, index) => (
                                        <Box
                                            key={`${photoUrl}-${index}`}
                                            component="img"
                                            src={photoUrl}
                                            alt={`Фото объявления ${index + 1}`}
                                            onClick={() => openPhotoModal(validPhotos, index)}
                                            sx={{
                                                width: '100%',
                                                height: 180,
                                                objectFit: 'cover',
                                                borderRadius: 1.5,
                                                cursor: 'pointer',
                                                transition: 'opacity 0.2s ease',
                                                '&:hover': {
                                                    opacity: 0.85
                                                }
                                            }}
                                        />
                                    ))}
                                </Box>
                            )}
                        </CardContent>
                    </Card>

                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Текущий статус
                            </Typography>
                            <StatusBlock
                                title="Текущий статус"
                                value={record.status}
                                onPhotoClick={openPhotoModal}
                            />
                            <Box sx={{ mt: 2 }}>
                                {statusEventsLoading ? (
                                    <Typography color="text.secondary">Загрузка автора статуса...</Typography>
                                ) : !hasLoadedStatusEvents ? (
                                    <Typography color="text.secondary">
                                        Автор статуса загрузится после раскрытия истории установки статусов
                                    </Typography>
                                ) : currentStatusEvent ? (
                                    <Box>
                                        <ProfileRow
                                            label="Кто поставил статус"
                                            value={formatActorLabel(currentStatusEvent.actor)}
                                        />
                                        <ProfileRow
                                            label="Дата простановки"
                                            value={formatDateTime(currentStatusEvent.createdAt)}
                                        />
                                        <ProfileRow
                                            label="Источник"
                                            value={getStatusEventSourceLabel(currentStatusEvent.source)}
                                        />
                                        {currentStatusEvent.reason ? (
                                            <ProfileRow
                                                label="Внутренняя причина"
                                                value={formatValue(currentStatusEvent.reason)}
                                            />
                                        ) : null}
                                    </Box>
                                ) : (
                                    <Typography color="text.secondary">
                                        Автор текущего статуса не найден в аудите
                                    </Typography>
                                )}
                            </Box>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                История статусов
                            </Typography>
                            {statusHistory.length === 0 ? (
                                <Typography color="text.secondary">История статусов отсутствует</Typography>
                            ) : (
                                <Box sx={{ display: 'grid', gap: 1.5 }}>
                                    {statusHistory.map((statusItem, index) => (
                                        <StatusBlock
                                            key={`${statusItem.type}-${index}`}
                                            title={`История #${index + 1}`}
                                            value={statusItem}
                                            onPhotoClick={openPhotoModal}
                                            hideEmptyFields
                                        />
                                    ))}
                                </Box>
                            )}
                        </CardContent>
                    </Card>

                    {canManageAdAttributes ? (
                        <Card>
                            <CardContent sx={{ p: 0 }}>
                                <Accordion
                                    expanded={attributesExpanded}
                                    onChange={(_, expanded) => setAttributesExpanded(expanded)}
                                    disableGutters
                                    elevation={0}
                                >
                                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                        <Typography variant="h6">Изменить аттрибуты объявления</Typography>
                                    </AccordionSummary>
                                    <AccordionDetails sx={{ pt: 0 }}>
                                        <Box sx={{ display: 'grid', gap: 2 }}>
                                            <Box>
                                                <Typography fontWeight={600} sx={{ mb: 1 }}>
                                                    Порядок фотографий
                                                </Typography>
                                                {!canReorderPhotos ? (
                                                    <Alert severity="info">
                                                        Перестановка фото недоступна: отсутствует `photosId` или нарушено соответствие с `attachmentPhotos`.
                                                    </Alert>
                                                ) : editedPhotoItems.length <= 1 ? (
                                                    <Typography color="text.secondary">Для перестановки нужно минимум 2 фото</Typography>
                                                ) : (
                                                    <Box sx={{ display: 'grid', gap: 1, gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))' } }}>
                                                        {editedPhotoItems.map((photoItem, index) => {
                                                            const isDragOver = photoDragOverId === photoItem.id && photoDragId && photoDragId !== photoItem.id;
                                                            const isDragging = photoDragId === photoItem.id;
                                                            const isFirst = index === 0;
                                                            const isLast = index === editedPhotoItems.length - 1;

                                                            return (
                                                                <Box
                                                                    key={photoItem.id}
                                                                    draggable
                                                                    onDragStart={() => handlePhotoDragStart(photoItem.id)}
                                                                    onDragOver={(event) => handlePhotoDragOver(photoItem.id, event)}
                                                                    onDrop={(event) => handlePhotoDrop(photoItem.id, event)}
                                                                    onDragEnd={handlePhotoDragEnd}
                                                                    sx={{
                                                                        border: '1px solid',
                                                                        borderColor: isDragOver ? 'primary.main' : 'divider',
                                                                        borderRadius: 1.5,
                                                                        p: 1,
                                                                        bgcolor: isDragging ? 'action.selected' : 'background.paper',
                                                                        opacity: isDragging ? 0.7 : 1
                                                                    }}
                                                                >
                                                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75 }}>
                                                                        <Chip label={`#${index + 1}`} size="small" />
                                                                        <DragIndicatorIcon fontSize="small" color="disabled" />
                                                                    </Box>
                                                                    {photoItem.url ? (
                                                                        <Box
                                                                            component="img"
                                                                            src={photoItem.url}
                                                                            alt={`Фото ${index + 1}`}
                                                                            onClick={() => openPhotoModal(editedPhotoItems.map((item) => item.url), index)}
                                                                            sx={{
                                                                                width: '100%',
                                                                                height: 120,
                                                                                objectFit: 'cover',
                                                                                borderRadius: 1,
                                                                                cursor: 'pointer'
                                                                            }}
                                                                        />
                                                                    ) : (
                                                                        <Box
                                                                            sx={{
                                                                                height: 120,
                                                                                borderRadius: 1,
                                                                                bgcolor: 'action.hover',
                                                                                display: 'flex',
                                                                                alignItems: 'center',
                                                                                justifyContent: 'center',
                                                                                color: 'text.secondary'
                                                                            }}
                                                                        >
                                                                            Без фото
                                                                        </Box>
                                                                    )}
                                                                    <Box sx={{ mt: 0.75, display: 'flex', justifyContent: 'space-between' }}>
                                                                        <IconButton
                                                                            size="small"
                                                                            onClick={() => moveEditablePhotoByOffset(photoItem.id, -1)}
                                                                            disabled={isFirst}
                                                                        >
                                                                            <ArrowBackIcon fontSize="small" />
                                                                        </IconButton>
                                                                        <IconButton
                                                                            size="small"
                                                                            onClick={() => moveEditablePhotoByOffset(photoItem.id, 1)}
                                                                            disabled={isLast}
                                                                        >
                                                                            <ArrowForwardIcon fontSize="small" />
                                                                        </IconButton>
                                                                    </Box>
                                                                </Box>
                                                            );
                                                        })}
                                                    </Box>
                                                )}
                                                {hasPhotoReorderSource ? (
                                                    <Typography color="text.secondary" variant="body2" sx={{ mt: 1 }}>
                                                        На компьютере перетаскивайте фото мышью, на мобилке используйте стрелки.
                                                    </Typography>
                                                ) : null}
                                            </Box>

                                            <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
                                                <TextField
                                                    fullWidth
                                                    label="Название бренда"
                                                    value={editBrandName}
                                                    onChange={(event) => setEditBrandName(event.target.value)}
                                                    error={Boolean(brandNameError)}
                                                    helperText={brandNameError || ' '}
                                                />
                                                <TextField
                                                    fullWidth
                                                    label="Модель"
                                                    value={editModelName}
                                                    onChange={(event) => setEditModelName(event.target.value)}
                                                    error={Boolean(modelNameError)}
                                                    helperText={modelNameError || ' '}
                                                />
                                            </Box>

                                            <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
                                                <FormControlLabel
                                                    control={(
                                                        <Switch
                                                            checked={editIsExchange}
                                                            onChange={(event) => setEditIsExchange(event.target.checked)}
                                                        />
                                                    )}
                                                    label="Возможен обмен"
                                                />
                                                <FormControlLabel
                                                    control={(
                                                        <Switch
                                                            checked={editIsVintage}
                                                            onChange={(event) => setEditIsVintage(event.target.checked)}
                                                        />
                                                    )}
                                                    label="Винтаж"
                                                />
                                                <FormControlLabel
                                                    control={(
                                                        <Switch
                                                            checked={editIsExistBox}
                                                            onChange={(event) => setEditIsExistBox(event.target.checked)}
                                                        />
                                                    )}
                                                    label="Есть коробка"
                                                />
                                                <TextField
                                                    fullWidth
                                                    label="Серийный номер"
                                                    value={editSerialNumber}
                                                    onChange={(event) => setEditSerialNumber(event.target.value)}
                                                    error={Boolean(serialNumberError)}
                                                    helperText={serialNumberError || ' '}
                                                />
                                            </Box>

                                            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                                <Button
                                                    variant="contained"
                                                    onClick={handleOpenAttributesConfirmDialog}
                                                    disabled={disableAttributesSaveButton}
                                                >
                                                    Изменить
                                                </Button>
                                            </Box>
                                        </Box>
                                    </AccordionDetails>
                                </Accordion>
                            </CardContent>
                        </Card>
                    ) : null}

                    <Card>
                        <CardContent sx={{ p: 0 }}>
                            <Accordion
                                expanded={statusEventsExpanded}
                                onChange={(_, expanded) => setStatusEventsExpanded(expanded)}
                                disableGutters
                                elevation={0}
                            >
                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                    <Typography variant="h6">История установки статусов</Typography>
                                </AccordionSummary>
                                <AccordionDetails sx={{ pt: 0 }}>
                                    {statusEventsLoading ? (
                                        <Typography color="text.secondary">Загрузка истории...</Typography>
                                    ) : !hasLoadedStatusEvents ? (
                                        <Typography color="text.secondary">Раскройте секцию, чтобы загрузить историю установки статусов</Typography>
                                    ) : statusEvents.length === 0 ? (
                                        <Typography color="text.secondary">История установки статусов отсутствует</Typography>
                                    ) : (
                                        <Box sx={{ display: 'grid', gap: 1.5 }}>
                                            {statusEvents.map((statusEvent, index) => {
                                                const statusMeta = getAdStatusMeta(statusEvent.statusType);
                                                const isAttributesEvent = normalizeAdStatus(statusEvent.source) === 'AD_ATTRIBUTES_UPDATE';

                                                return (
                                                    <Box
                                                        key={statusEvent.id || `${statusEvent.statusType || 'status'}-${statusEvent.createdAt || index}`}
                                                        sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 1.5 }}
                                                    >
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                                                            <Chip label={statusMeta.label} color={statusMeta.color} size="small" />
                                                        </Box>
                                                        <ProfileRow
                                                            label={isAttributesEvent ? 'Кто изменил атрибуты' : 'Кто поставил статус'}
                                                            value={formatActorLabel(statusEvent.actor)}
                                                        />
                                                        <ProfileRow
                                                            label={isAttributesEvent ? 'Дата изменения' : 'Дата простановки'}
                                                            value={formatDateTime(statusEvent.createdAt)}
                                                        />
                                                        <ProfileRow
                                                            label="Источник"
                                                            value={getStatusEventSourceLabel(statusEvent.source)}
                                                        />
                                                        {statusEvent.reason ? (
                                                            <ProfileRow
                                                                label="Внутренняя причина"
                                                                value={formatValue(statusEvent.reason)}
                                                            />
                                                        ) : null}
                                                        {renderAttributeChangeRows(statusEvent)}
                                                    </Box>
                                                );
                                            })}
                                        </Box>
                                    )}
                                </AccordionDetails>
                            </Accordion>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardContent sx={{ p: 0 }}>
                            <Accordion
                                expanded={blockHistoryExpanded}
                                onChange={(_, expanded) => setBlockHistoryExpanded(expanded)}
                                disableGutters
                                elevation={0}
                            >
                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                    <Typography variant="h6">История блокировки и разблокировки</Typography>
                                </AccordionSummary>
                                <AccordionDetails sx={{ pt: 0 }}>
                                    {blockEventsLoading ? (
                                        <Typography color="text.secondary">Загрузка истории...</Typography>
                                    ) : blockEvents.length === 0 ? (
                                        <Typography color="text.secondary">История блокировки и разблокировки отсутствует</Typography>
                                    ) : (
                                        <Box sx={{ display: 'grid', gap: 1.5 }}>
                                            {blockEvents.map((event, index) => {
                                                const normalizedAction = normalizeAdStatus(event.action);
                                                const isUnblockAction = normalizedAction === 'UNBLOCK';
                                                const actionLabel = isUnblockAction ? 'Разблокировка' : 'Блокировка';
                                                const actionColor = isUnblockAction ? 'warning' : 'error';
                                                const moderatorName = (event.moderator?.name || '').trim();
                                                const moderatorLogin = (event.moderator?.login || '').trim();
                                                const moderatorLabel = moderatorName && moderatorLogin
                                                    ? `${moderatorName} (${moderatorLogin})`
                                                    : (moderatorName || moderatorLogin || 'Не найдено');
                                                const actionDateLabel = event.createdAt ? formatDateTime(event.createdAt) : 'Не найдено';

                                                return (
                                                    <Box
                                                        key={event.id || `${event.action || 'event'}-${event.createdAt || index}`}
                                                        sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 1.5 }}
                                                    >
                                                        <Box sx={{ mb: 1 }}>
                                                            <Chip label={actionLabel} color={actionColor} size="small" />
                                                        </Box>
                                                        <ProfileRow
                                                            label={isUnblockAction ? 'Кто разблокировал' : 'Кто заблокировал'}
                                                            value={moderatorLabel}
                                                        />
                                                        <ProfileRow
                                                            label={isUnblockAction ? 'Дата разблокировки' : 'Дата блокировки'}
                                                            value={actionDateLabel}
                                                        />
                                                    </Box>
                                                );
                                            })}
                                        </Box>
                                    )}
                                </AccordionDetails>
                            </Accordion>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Служебная информация
                            </Typography>
                            <ProfileRow label="ID объявления" value={record.adObjectId || record.id} />
                            <ProfileRow
                                label="ID пользователя"
                                value={
                                    <Link
                                        component="button"
                                        type="button"
                                        underline="hover"
                                        onClick={() => redirect('show', 'users', record.createdByUserId)}
                                        sx={{ cursor: 'pointer' }}
                                    >
                                        {record.createdByUserId}
                                    </Link>
                                }
                            />
                            <ProfileRow label="Оригинальный лейбл" value={formatBoolean(record.hasOriginalLabel)} />
                            <ProfileRow label="Дата создания" value={formatDateTime(record.creationDate)} />
                            <ProfileRow label="Последнее обновление" value={formatDateTime(record.lastUpdateDate)} />
                        </CardContent>
                    </Card>

                    {canManageAdBlock || canManageAdStatus ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                            {canManageAdBlock ? (
                                <Button
                                    variant="contained"
                                    color={isBlocked ? 'warning' : 'error'}
                                    onClick={isBlocked ? handleOpenUnblockDialog : handleOpenBlockDialog}
                                    startIcon={isBlocked ? <LockOpenIcon /> : <BlockIcon />}
                                    disabled={blockSubmitting || statusDialogSubmitting}
                                >
                                    {isBlocked ? 'Разблокировать объявление' : 'Заблокировать объявление'}
                                </Button>
                            ) : null}
                            {canManageAdStatus ? (
                                <Tooltip title={statusChangeDisabledHint} disableHoverListener={!statusChangeDisabledHint}>
                                    <span>
                                        <Button
                                            variant="outlined"
                                            onClick={handleOpenStatusDialog}
                                            disabled={disableStatusChangeButton}
                                        >
                                            Установить другой статус
                                        </Button>
                                    </span>
                                </Tooltip>
                            ) : null}
                        </Box>
                    ) : null}
                </Box>

                <Modal open={photoModalOpen} onClose={handleClosePhotoModal}>
                    <Box
                        sx={{
                            position: 'fixed',
                            inset: 0,
                            bgcolor: 'rgba(0, 0, 0, 0.92)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            p: 2
                        }}
                    >
                        <IconButton
                            onClick={handleClosePhotoModal}
                            sx={{
                                position: 'absolute',
                                top: 12,
                                right: 12,
                                color: 'white',
                                bgcolor: 'rgba(0, 0, 0, 0.45)',
                                '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.65)' }
                            }}
                        >
                            <CloseIcon />
                        </IconButton>

                        {modalPhotos.length > 1 && (
                            <IconButton
                                onClick={handlePrevPhoto}
                                sx={{
                                    position: 'absolute',
                                    left: 12,
                                    color: 'white',
                                    bgcolor: 'rgba(0, 0, 0, 0.45)',
                                    '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.65)' }
                                }}
                            >
                                <NavigateBeforeIcon />
                            </IconButton>
                        )}

                        {modalPhotos[currentPhotoIndex] && (
                            <Box
                                component="img"
                                src={modalPhotos[currentPhotoIndex]}
                                alt={`Фото ${currentPhotoIndex + 1}`}
                                sx={{
                                    maxWidth: '95vw',
                                    maxHeight: '90vh',
                                    objectFit: 'contain',
                                    borderRadius: 1
                                }}
                            />
                        )}

                        {modalPhotos.length > 1 && (
                            <IconButton
                                onClick={handleNextPhoto}
                                sx={{
                                    position: 'absolute',
                                    right: 12,
                                    color: 'white',
                                    bgcolor: 'rgba(0, 0, 0, 0.45)',
                                    '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.65)' }
                                }}
                            >
                                <NavigateNextIcon />
                            </IconButton>
                        )}

                        {modalPhotos.length > 1 && (
                            <Typography
                                sx={{
                                    position: 'absolute',
                                    bottom: 16,
                                    color: 'white',
                                    fontWeight: 600
                                }}
                            >
                                {currentPhotoIndex + 1} / {modalPhotos.length}
                            </Typography>
                        )}
                    </Box>
                </Modal>

                <Dialog open={blockDialogOpen} onClose={handleCloseBlockDialog} maxWidth="sm" fullWidth>
                    <DialogTitle>Заблокировать объявление</DialogTitle>
                    <DialogContent sx={{ display: 'grid', gap: 1.5, pt: '12px !important' }}>
                        <Alert severity="warning">
                            Причина блокировки будет видна пользователю.
                        </Alert>
                        <TextField
                            multiline
                            minRows={3}
                            fullWidth
                            label="Причина блокировки (видна пользователю)"
                            value={blockReason}
                            onChange={(event) => setBlockReason(event.target.value)}
                        />

                        <Alert severity="info">
                            Внутренний комментарий не виден пользователю.
                        </Alert>
                        <TextField
                            multiline
                            minRows={3}
                            fullWidth
                            label="Внутренний комментарий (не виден пользователю)"
                            value={blockInternalComment}
                            onChange={(event) => setBlockInternalComment(event.target.value)}
                        />

                        {blockDialogError && (
                            <Typography color="error" variant="body2">
                                {blockDialogError}
                            </Typography>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCloseBlockDialog} disabled={blockSubmitting}>
                            Отмена
                        </Button>
                        <Button
                            variant="contained"
                            color="error"
                            onClick={handleSubmitBlock}
                            disabled={blockSubmitting}
                        >
                            {blockSubmitting ? 'Блокируем...' : 'Заблокировать'}
                        </Button>
                    </DialogActions>
                </Dialog>

                <Dialog open={unblockDialogOpen} onClose={handleCloseUnblockDialog} maxWidth="xs" fullWidth>
                    <DialogTitle>Разблокировать объявление</DialogTitle>
                    <DialogContent sx={{ pt: '12px !important' }}>
                        <Typography>
                            Подтвердите разблокировку объявления.
                        </Typography>
                        {blockDialogError && (
                            <Typography color="error" variant="body2" sx={{ mt: 1.5 }}>
                                {blockDialogError}
                            </Typography>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCloseUnblockDialog} disabled={blockSubmitting}>
                            Отмена
                        </Button>
                        <Button
                            variant="contained"
                            color="warning"
                            onClick={handleSubmitUnblock}
                            disabled={blockSubmitting}
                        >
                            {blockSubmitting ? 'Разблокируем...' : 'Разблокировать'}
                        </Button>
                    </DialogActions>
                </Dialog>

                <Dialog open={attributesConfirmOpen} onClose={handleCloseAttributesConfirmDialog} maxWidth="md" fullWidth>
                    <DialogTitle>Подтверждение изменений атрибутов</DialogTitle>
                    <DialogContent sx={{ display: 'grid', gap: 1.5, pt: '12px !important' }}>
                        {changePreviewItems.length === 0 ? (
                            <Typography color="text.secondary">Изменений нет</Typography>
                        ) : (
                            <Box sx={{ display: 'grid', gap: 1.5 }}>
                                {changePreviewItems.map((changeItem, index) => (
                                    <Box
                                        key={`${changeItem.label}-${index}`}
                                        sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5, p: 1.25 }}
                                    >
                                        <Typography fontWeight={600} sx={{ mb: 0.5 }}>
                                            {changeItem.label}
                                        </Typography>
                                        {changeItem.kind === 'text' ? (
                                            <Typography sx={{ whiteSpace: 'pre-wrap' }}>
                                                {formatValue(changeItem.before)} → {formatValue(changeItem.after)}
                                            </Typography>
                                        ) : changeItem.kind === 'boolean' ? (
                                            <Typography sx={{ whiteSpace: 'pre-wrap' }}>
                                                {formatBoolean(changeItem.before)} → {formatBoolean(changeItem.after)}
                                            </Typography>
                                        ) : (
                                            <Box sx={{ display: 'grid', gap: 1 }}>
                                                <PhotoOrderPreview title="Было" photos={changeItem.before} onPhotoClick={openPhotoModal} />
                                                <PhotoOrderPreview title="Стало" photos={changeItem.after} onPhotoClick={openPhotoModal} />
                                            </Box>
                                        )}
                                    </Box>
                                ))}
                            </Box>
                        )}
                        {attributesSubmitError ? (
                            <Typography color="error" variant="body2">
                                {attributesSubmitError}
                            </Typography>
                        ) : null}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCloseAttributesConfirmDialog} disabled={attributesSubmitting}>
                            Отмена
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleSubmitAttributesUpdate}
                            disabled={attributesSubmitting || changePreviewItems.length === 0}
                        >
                            {attributesSubmitting ? 'Сохраняем...' : 'Подтвердить и изменить'}
                        </Button>
                    </DialogActions>
                </Dialog>

                <Dialog open={statusDialogOpen} onClose={handleCloseStatusDialog} maxWidth="sm" fullWidth>
                    <DialogTitle>Установить другой статус</DialogTitle>
                    <DialogContent sx={{ display: 'grid', gap: 1.5, pt: '12px !important' }}>
                        <FormControl fullWidth>
                            <InputLabel id="ad-status-dialog-status-label">Новый статус</InputLabel>
                            <Select
                                labelId="ad-status-dialog-status-label"
                                label="Новый статус"
                                value={statusDialogValue}
                                onChange={(event) => {
                                    setStatusDialogValue(event.target.value as AdStatusUpdateTarget);
                                }}
                                disabled={statusDialogSubmitting}
                            >
                                <MenuItem value="ACTIVE">Опубликовать (активный статус)</MenuItem>
                                <MenuItem value="MODERATION">Отправить на модерацию</MenuItem>
                                <MenuItem value="EXPERT_MODERATION">Отправить экспертам</MenuItem>
                            </Select>
                        </FormControl>

                        {statusDialogValue === 'ACTIVE' ? (
                            <FormControl fullWidth>
                                <InputLabel id="ad-status-dialog-original-label">Лейбл оригинальности</InputLabel>
                                <Select
                                    labelId="ad-status-dialog-original-label"
                                    label="Лейбл оригинальности"
                                    value={statusDialogOriginalLabel}
                                    onChange={(event) => {
                                        setStatusDialogOriginalLabel(event.target.value as OriginalLabelDialogValue);
                                    }}
                                    disabled={statusDialogSubmitting}
                                >
                                    <MenuItem value="UNCHANGED">Не менять лейбл</MenuItem>
                                    <MenuItem value="SET_TRUE">Установить лейбл оригинальности</MenuItem>
                                    <MenuItem value="SET_FALSE">Убрать лейбл оригинальности</MenuItem>
                                </Select>
                            </FormControl>
                        ) : null}

                        <Alert severity="info">
                            Причина используется только для внутренней аналитики и не отображается пользователю.
                        </Alert>
                        <TextField
                            multiline
                            minRows={3}
                            fullWidth
                            label="Причина установки статуса (обязательно)"
                            value={statusDialogReason}
                            onChange={(event) => setStatusDialogReason(event.target.value)}
                            disabled={statusDialogSubmitting}
                        />

                        {statusDialogError && (
                            <Typography color="error" variant="body2">
                                {statusDialogError}
                            </Typography>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCloseStatusDialog} disabled={statusDialogSubmitting}>
                            Отмена
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleSubmitStatus}
                            disabled={statusDialogSubmitting}
                        >
                            {statusDialogSubmitting ? 'Сохраняем...' : 'Установить статус'}
                        </Button>
                    </DialogActions>
                </Dialog>

                {isBlocked && (
                    <Alert
                        severity="error"
                        variant="filled"
                        sx={{
                            position: 'fixed',
                            right: 20,
                            bottom: 20,
                            zIndex: (theme) => theme.zIndex.snackbar,
                            boxShadow: 3
                        }}
                    >
                        Объявление заблокировано
                    </Alert>
                )}
            </SimpleShowLayout>
        </Show>
    );
};
